import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { NumberInput } from '../../components/NumberInput';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  Plus, Search, Loader2, ClipboardList, Trash2, Save, RefreshCw, FileText,
  CheckCircle, Clock, XCircle, ChevronLeft, ChevronRight, Filter, Eye, Pencil, Unlock,
  AlertTriangle, Database, Globe, ArrowRight, ShoppingCart, Check, Package, Send,
  Printer, FileSpreadsheet, Truck,
} from 'lucide-react';
import { toast } from 'sonner';
import { TableSkeleton } from '../../lib/skeletons';
import { useAuth } from '../../lib/auth';
import PendientesCompra from './PendientesCompra';
import PendientesRecepcion from './PendientesRecepcion';
import { exportToCSV, printTable } from '../../lib/printUtils';

const PAGE_SIZE = 50;

const parseDate = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim().replace(/\s+0$/, '');
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (m) {
    let y = parseInt(m[3], 10);
    if (y < 100) y += y > 50 ? 1900 : 2000;
    return new Date(y, parseInt(m[1], 10) - 1, parseInt(m[2], 10));
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};
const fmtDate = (raw) => {
  const d = parseDate(raw);
  return d ? d.toLocaleDateString('es-CL') : '-';
};

const ORIGEN_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'access', label: 'Access' },
  { value: 'web', label: 'Web' },
];
const ESTADO_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'pendiente', label: 'Pendientes' },
  { value: 'parcial', label: 'Parcial' },
  { value: 'aprobada', label: 'Aprobadas' },
  { value: 'en_proceso', label: 'En Proceso' },
  { value: 'rechazada', label: 'Rechazadas' },
];

const ROLES_APROBACION = ['admin', 'gerencia', 'jefe_proyectos'];

// Autocomplete for OT number (reused from GenerarOC pattern)
function OtAutocomplete({ value, onChange, label = "OT" }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSugg, setShowSugg] = useState(false);
  const debRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowSugg(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    onChange(val);
    if (debRef.current) clearTimeout(debRef.current);
    if (val.length < 1) { setSuggestions([]); setShowSugg(false); return; }
    debRef.current = setTimeout(async () => {
      try {
        const res = await api.get(`/compras/ots-activas?q=${encodeURIComponent(val)}&limit=15`);
        setSuggestions(res.data.ots || []);
        setShowSugg(true);
      } catch { setSuggestions([]); }
    }, 300);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <label className="block text-xs font-medium mb-1">{label}</label>
      <Input placeholder="N° OT (escriba para buscar)" value={value} onChange={handleChange}
        onFocus={() => { if (suggestions.length > 0) setShowSugg(true); }}
        data-testid="input-ot" />
      {showSugg && suggestions.length > 0 && (
        <div className="absolute z-50 w-72 mt-0.5 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
          {suggestions.map((ot) => (
            <button key={ot.numero_ot} onClick={() => { onChange(String(ot.numero_ot)); setShowSugg(false); setSuggestions([]); }}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 border-b last:border-0">
              <span className="font-mono font-bold text-blue-700">OT-{ot.numero_ot}</span>
              <span className={`ml-2 text-[10px] px-1 rounded ${ot.estado === 'EN EJECUCION' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                {ot.estado}
              </span>
              {ot.descripcion && <span className="block text-[10px] text-gray-500 truncate">{ot.descripcion}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Autocomplete for Solicitante
function ReqSolicitanteAutocomplete({ value, onChange }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSugg, setShowSugg] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowSugg(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const loadSolicitantes = async () => {
    if (loaded) return;
    try {
      const res = await api.get('/compras/solicitantes');
      setSuggestions(res.data.solicitantes || []);
      setLoaded(true);
    } catch { /* ignore */ }
  };

  const filtered = suggestions.filter(s => !value || s.toLowerCase().includes(value.toLowerCase()));

  return (
    <div className="relative" ref={wrapRef}>
      <label className="block text-xs font-medium mb-1">Solicitante *</label>
      <Input placeholder="Nombre (escriba o seleccione)" value={value}
        onChange={(e) => { onChange(e.target.value); setShowSugg(true); }}
        onFocus={() => { loadSolicitantes(); setShowSugg(true); }}
        data-testid="input-solicitante" />
      {showSugg && filtered.length > 0 && (
        <div className="absolute z-50 w-full mt-0.5 bg-white border rounded-md shadow-lg max-h-40 overflow-y-auto">
          {filtered.map((s, i) => (
            <button key={`sol-${i}`} onClick={() => { onChange(s); setShowSugg(false); }}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 border-b last:border-0">
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function OrigenBadge({ origen }) {
  if (origen === 'access') return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-sky-100 text-sky-700 border border-sky-200" data-testid="badge-access">
      <Database className="w-3 h-3" /> Access
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200" data-testid="badge-web">
      <Globe className="w-3 h-3" /> Web
    </span>
  );
}

function EstadoBadge({ estado }) {
  const map = {
    pendiente: { bg: 'bg-amber-100 text-amber-800 border-amber-200', icon: Clock },
    aprobada: { bg: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
    parcial: { bg: 'bg-indigo-100 text-indigo-800 border-indigo-200', icon: AlertTriangle },
    en_proceso: { bg: 'bg-blue-100 text-blue-800 border-blue-200', icon: Loader2 },
    completada: { bg: 'bg-teal-100 text-teal-800 border-teal-200', icon: CheckCircle },
    rechazada: { bg: 'bg-red-100 text-red-800 border-red-200', icon: XCircle },
    // Iter 433 (Renzo): las requisiciones cerradas por OC se guardan como
    // 'historico' pero el usuario las conoce como "Completadas".
    historico: { bg: 'bg-teal-100 text-teal-800 border-teal-200', icon: CheckCircle },
  };
  const cfg = map[estado] || map.pendiente;
  const Icon = cfg.icon;
  const label = estado === 'en_proceso' ? 'En Proceso' : estado === 'historico' ? 'Completada' : estado?.charAt(0).toUpperCase() + estado?.slice(1);
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium border ${cfg.bg}`} data-testid={`estado-badge-${estado}`}>
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}

function PrioridadBadge({ prioridad }) {
  if (prioridad === 'muy_urgente') return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-red-200 text-red-800 border border-red-300">MUY URGENTE</span>;
  if (prioridad === 'urgente') return <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-orange-100 text-orange-700 border border-orange-200">Urgente</span>;
  return <span className="px-2 py-0.5 rounded text-[10px] bg-gray-100 text-gray-600">Normal</span>;
}

// Iter 313.b — Chip "Alertado a Gerencia": visible cuando el cron ya escaló
// esta requisición por retraso (>3 días pendiente).
function AlertadoGerenciaBadge({ ts, modo, testId }) {
  if (!ts) return null;
  let dias = null;
  try {
    const then = new Date(ts);
    const now = new Date();
    dias = Math.max(0, Math.floor((now - then) / (1000 * 60 * 60 * 24)));
  } catch { /* noop */ }
  const label = modo === 'semanal' ? 'Resumen semanal' : 'Alerta individual';
  const tooltip = `Escalado a Gerencia hace ${dias ?? '?'} día(s) — ${label}`;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-300 whitespace-nowrap"
      title={tooltip}
      data-testid={testId}
    >
      <AlertTriangle className="w-3 h-3" /> Alertado a Gerencia
    </span>
  );
}

function KpiCard({ title, value, icon: Icon, color, testId }) {
  return (
    <Card className="shadow-sm" data-testid={testId}>
      <CardContent className="p-3 flex items-center gap-3">
        <div className={`p-2 rounded-lg ${color}`}><Icon className="w-5 h-5" /></div>
        <div>
          <p className="text-[11px] text-muted-foreground leading-tight">{title}</p>
          <p className="text-xl font-bold">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Requisiciones() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const acciones = user?.permisos?.compras?.acciones || {};
  // Iter 236.w (Renzo, feb-2026): la aprobación de requisiciones ya NO se
  // otorga automáticamente por el rol 'compras'. Solo admin/gerencia (dueños
  // del sistema) o usuarios con la acción granular `requisiciones_aprobar`
  // pueden autorizar. Alfredo Solis (rol=compras sin acción) NO debe ver el
  // botón. El default de la plantilla rol=compras sigue trayendo la acción
  // marcada al crear usuarios nuevos — pero los que ya existen sin esa acción
  // se respetan.
  const canApprove = ROLES_APROBACION.includes(user?.rol)
    || ['admin', 'gerencia'].includes(user?.rol)
    || acciones.requisiciones_aprobar === true;
  const canEdit = ['admin', 'gerencia', 'compras'].includes(user?.rol)
    || acciones.requisiciones_editar === true
    || user?.permisos?.compras?.editar === true;
  const canDesbloquear = ['admin', 'gerencia'].includes(user?.rol)
    || acciones.requisiciones_desbloquear === true;

  const [activeTab, setActiveTab] = useState('lista');
  const [data, setData] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  // Filters
  const [filterOrigen, setFilterOrigen] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [filterOt, setFilterOt] = useState('');
  const [search, setSearch] = useState('');
  const searchTimer = useRef(null);
  // Iter 433 (Renzo): vista Activas (default) / Completadas (requisiciones ya atendidas)
  const [vista, setVista] = useState('activas');

  // Detail modal
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  // Approval modal
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [approvalObs, setApprovalObs] = useState('');
  const [approving, setApproving] = useState(false);
  const [approvalTarget, setApprovalTarget] = useState(null);
  const [approvalItemStates, setApprovalItemStates] = useState([]); // per-item: 'aprobar'|'rechazar'|null

  // Cart: selected items for OC generation
  const [cartItems, setCartItems] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);

  // New requisition form
  const [otNumero, setOtNumero] = useState('');

  // Iter 292.j (Carlos, feb-2026) — Trazabilidad completa modal
  const [trazaOpen, setTrazaOpen] = useState(false);
  const [trazaLoading, setTrazaLoading] = useState(false);
  const [trazaData, setTrazaData] = useState(null);
  const [trazaRow, setTrazaRow] = useState(null);
  const openTrazabilidad = async (row) => {
    setTrazaOpen(true);
    setTrazaRow(row);
    setTrazaData(null);
    setTrazaLoading(true);
    try {
      const res = await api.get(`/compras/requisiciones/${row.numero}/trazabilidad`);
      setTrazaData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo cargar la trazabilidad');
    } finally {
      setTrazaLoading(false);
    }
  };
  const closeTrazabilidad = () => {
    setTrazaOpen(false);
    setTrazaData(null);
    setTrazaRow(null);
  };
  // Auto-prellenado: usar el nombre del usuario logueado como solicitante por
  // defecto (Carlos: "agregaselos a todos"). El user puede editarlo igual.
  const [solicitante, setSolicitante] = useState(user?.nombre || '');
  const [prioridad, setPrioridad] = useState('normal');
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [matSearch, setMatSearch] = useState('');
  const [matResults, setMatResults] = useState([]);
  const [matLoading, setMatLoading] = useState(false);
  const [matModal, setMatModal] = useState(false);
  // Editar/Desbloquear (NEW)
  const [editingNumero, setEditingNumero] = useState(null); // null = crear nueva
  const [desbloquearOpen, setDesbloquearOpen] = useState(false);
  const [desbloquearMotivo, setDesbloquearMotivo] = useState('');
  const [desbloquearTarget, setDesbloquearTarget] = useState(null);
  const [desbloquearLoading, setDesbloquearLoading] = useState(false);

  // Si `user` se hidrata después del primer render (caso AuthProvider lazy)
  // y el campo solicitante está vacío y NO estamos editando una req existente,
  // autocompletamos con el nombre del usuario logueado.
  useEffect(() => {
    if (user?.nombre && !solicitante && editingNumero === null) {
      setSolicitante(user.nombre);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.nombre]);

  const cargarParaEditar = async (row) => {
    if (row.origen !== 'web') {
      toast.info('Solo se pueden editar requisiciones web');
      return;
    }
    try {
      const res = await api.get(`/compras/requisiciones/${row.numero}`);
      const req = res.data;
      const estado = (req.estado || '').toLowerCase();
      if (!['pendiente', 'desbloqueada'].includes(estado)) {
        toast.error(`No se puede editar (estado: ${estado}). Solicite desbloqueo a Compras/Gerencia.`);
        return;
      }
      setEditingNumero(req.numero);
      setOtNumero(req.ot_numero ? String(req.ot_numero) : '');
      setSolicitante(req.solicitante || '');
      setPrioridad(req.prioridad || 'normal');
      setItems((req.items || []).map((it, idx) => ({
        key: `e-${idx}`,
        descripcion: it.descripcion || '',
        cantidad: it.cantidad || 1,
        unidad: it.unidad || 'UND',
        codigo_material: it.codigo_material || '',
        observaciones: it.observaciones || '',
        ot_numero: it.ot_numero || '',
      })));
      setActiveTab('nueva');
      toast.success(`Editando requisición #${req.numero}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cargar requisición');
    }
  };

  const cancelarEdicion = () => {
    setEditingNumero(null);
    setItems([]); setSolicitante(user?.nombre || ''); setOtNumero(''); setPrioridad('normal');
  };

  const abrirDesbloqueoDialog = (row) => {
    if (row.origen !== 'web') {
      toast.info('Solo se pueden desbloquear requisiciones web');
      return;
    }
    setDesbloquearTarget(row);
    setDesbloquearMotivo('');
    setDesbloquearOpen(true);
  };

  const handleDesbloquear = async () => {
    if (desbloquearMotivo.trim().length < 5) {
      toast.error('El motivo debe tener al menos 5 caracteres');
      return;
    }
    setDesbloquearLoading(true);
    try {
      await api.post(`/compras/requisiciones/${desbloquearTarget.numero}/desbloquear`, {
        motivo: desbloquearMotivo.trim(),
      });
      toast.success(`Requisición #${desbloquearTarget.numero} desbloqueada`);
      setDesbloquearOpen(false);
      loadData(page);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al desbloquear');
    } finally {
      setDesbloquearLoading(false);
    }
  };

  const loadData = useCallback(async (currentPage = 0) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterOrigen) params.append('origen', filterOrigen);
      // Vista Completadas → requisiciones cerradas (estado 'historico'); de lo
      // contrario respeta el filtro de estado elegido (solo estados activos).
      const estadoParam = vista === 'completadas' ? 'historico' : filterEstado;
      if (estadoParam) params.append('estado', estadoParam);
      if (filterOt) params.append('ot', filterOt);
      if (search) params.append('q', search);
      params.append('skip', String(currentPage * PAGE_SIZE));
      params.append('limit', String(PAGE_SIZE));
      const res = await api.get(`/compras/pedidos-requisiciones?${params}`);
      setData(res.data.pedidos_requisiciones || []);
      setTotal(res.data.total || 0);
      setStats(res.data.stats || {});
    } catch {
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  }, [filterOrigen, filterEstado, filterOt, search, vista]);

  useEffect(() => { setPage(0); loadData(0); }, [loadData]);

  // Iter 236.r4 (Carlos): imprimir requisición individual con sus items
  const imprimirRequisicionIndividual = () => {
    if (!detailData || !selectedRow) {
      toast.warning('No hay datos para imprimir');
      return;
    }
    const items = detailData.items || [];
    // Iter 292.c (Carlos, feb-2026): sello "NO APROBADA" mientras la
    // requisición no esté en estado 'aprobada'. Iter 306: 'historico' también
    // cuenta como aprobada (se archiva solo tras aprobación/compra completa).
    const estadoLower = String(selectedRow.estado || '').toLowerCase();
    const noAprobada = estadoLower !== 'aprobada' && estadoLower !== 'historico';
    // Iter 241 (Carlos #4): la impresión usaba campos inexistentes
    // (cantidad_solicitada/cantidad_con_oc) y el N° de línea salía NaN por un
    // índice mal pasado. Precomputamos las filas con los campos reales.
    const rows = items.map((it, i) => {
      const cant = Number(it.cantidad) || 0;
      const conOc = Number(it.cantidad_oc) || 0;
      return {
        _idx: i + 1,
        codigo: it.codigo || '',
        descripcion: it.descripcion || '',
        observaciones: it.observaciones || '',
        unidad: it.unidad || '',
        cantidad: cant,
        cantidad_oc: conOc,
        pendiente: Math.max(0, cant - conOc),
        avance_pct: cant > 0 ? Math.min(100, Math.round((conOc / cant) * 100)) : 0,
        ot_numero: it.ot_numero ?? selectedRow.ot_numero ?? '-',
        estado_item: it.estado_item || '',
      };
    });
    printTable({
      title: `Pedido / Requisición #${selectedRow.numero}`,
      subtitle: `Solicitante: ${selectedRow.solicitante || '-'} · OT ${selectedRow.ot_numero || '-'} · ${fmtDate(selectedRow.fecha)} · Estado: ${(selectedRow.estado || '').toUpperCase()}`,
      banner: noAprobada ? { text: 'NO APROBADA', color: '#c53030', bg: '#fff5f5' } : null,
      // Iter 292.d (Carlos, feb-2026): Requisiciones impresas con +15%
      // de tamaño de fuente para lectura más cómoda.
      fontScale: 1.15,
      summary: [
        { label: 'Origen', value: (selectedRow.origen || '').toUpperCase() },
        { label: 'Items totales', value: items.length },
        { label: 'Cliente', value: selectedRow.cliente || '-' },
        { label: 'Generado', value: new Date().toLocaleString('es-CL') },
      ],
      columns: [
        { key: '_idx', label: '#', align: 'center' },
        { key: 'codigo', label: 'Código', mono: true },
        {
          key: 'descripcion', label: 'Descripción', html: true,
          // Iter 319 (Carlos): incluir el detalle del ítem (observaciones) bajo la
          // descripción, igual que en el modal (ej: "65X50X8 mm labio simple")
          format: (v, r) => {
            const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
            const sub = r.observaciones
              ? `<div style="font-size:0.85em;color:#777;font-style:italic;">${esc(r.observaciones)}</div>`
              : '';
            return `${esc(v)}${sub}`;
          },
        },
        { key: 'unidad', label: 'Und', align: 'center' },
        { key: 'cantidad', label: 'Solicit.', align: 'right' },
        { key: 'cantidad_oc', label: 'Con OC', align: 'right' },
        { key: 'pendiente', label: 'Pendiente', align: 'right' },
        { key: 'ot_numero', label: 'OT', align: 'center' },
        { key: 'avance_pct', label: 'Avance %', align: 'right', format: (v) => `${v}%` },
        { key: 'estado_item', label: 'Aprob.', align: 'center', format: (v) => (v || '').toUpperCase() },
      ],
      rows,
    });
  };

  // Carlos (09/05/2026): Imprimir / Exportar Requisiciones (página actual)
  const imprimirRequisiciones = () => {
    if (!data.length) { toast.warning('No hay requisiciones para imprimir'); return; }
    printTable({
      title: 'Pedidos / Requisiciones',
      subtitle: `${data.length} registros · Página ${page + 1} · Generado ${new Date().toLocaleString('es-CL')}`,
      // Iter 292.d (Carlos, feb-2026): Requisiciones impresas con +15%
      // de tamaño de fuente para lectura más cómoda.
      fontScale: 1.15,
      summary: [
        { label: 'Total visibles', value: data.length },
        { label: 'Total general', value: total },
        { label: 'Filtro origen', value: filterOrigen || 'Todos' },
        { label: 'Filtro estado', value: filterEstado || 'Todos' },
      ],
      columns: [
        { key: 'numero', label: 'N°', mono: true, align: 'center' },
        { key: 'origen', label: 'Origen', align: 'center', format: (v) => (v || '').toUpperCase() },
        { key: 'fecha', label: 'Fecha', format: (v) => fmtDate(v) },
        { key: 'solicitante', label: 'Solicitante' },
        { key: 'ot_numero', label: 'OT', align: 'center' },
        { key: 'descripcion', label: 'Descripción' },
        { key: 'cantidad_items', label: 'Items', align: 'center', format: (v, r) => v ?? r.items?.length ?? 0 },
        { key: 'estado', label: 'Estado', align: 'center', format: (v) => (v || '').toUpperCase() },
      ],
      rows: data,
    });
  };

  const exportarRequisicionesExcel = () => {
    if (!data.length) { toast.warning('No hay requisiciones para exportar'); return; }
    exportToCSV({
      filename: `requisiciones_pag${page + 1}`,
      title: 'CHIRINO STEEL · Pedidos / Requisiciones',
      summary: [
        { label: 'Total visibles', value: data.length },
        { label: 'Total general', value: total },
        { label: 'Página', value: page + 1 },
        { label: 'Filtro origen', value: filterOrigen || 'Todos' },
        { label: 'Filtro estado', value: filterEstado || 'Todos' },
        { label: 'Generado', value: new Date().toLocaleString('es-CL') },
      ],
      columns: [
        { key: 'numero', label: 'Número' },
        { key: 'origen', label: 'Origen' },
        { key: 'fecha', label: 'Fecha', format: (v) => fmtDate(v) },
        { key: 'solicitante', label: 'Solicitante' },
        { key: 'ot_numero', label: 'OT' },
        { key: 'cliente', label: 'Cliente' },
        { key: 'descripcion', label: 'Descripción' },
        { key: 'cantidad_items', label: 'Cant. items', format: (v, r) => v ?? r.items?.length ?? 0 },
        { key: 'estado', label: 'Estado', format: (v) => (v || '').toUpperCase() },
        { key: 'aprobado_por', label: 'Aprobado por' },
        { key: 'fecha_aprobacion', label: 'Fecha aprobación' },
        { key: 'observaciones', label: 'Observaciones' },
      ],
      rows: data,
    });
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    loadData(newPage);
  };

  const handleSearch = (val) => {
    setSearch(val);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setPage(0); }, 400);
  };

  const openDetail = async (row) => {
    setSelectedRow(row);
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const res = await api.get(`/compras/pedidos-requisiciones/${row.origen}/${row.numero}/detalle`);
      setDetailData(res.data);
    } catch {
      toast.error('Error cargando detalle');
    } finally {
      setDetailLoading(false);
    }
  };

  const openApproval = (row, accion) => {
    setApprovalTarget({ ...row, accion });
    setApprovalObs('');
    // Init item states for partial approval
    if (accion === 'parcial' && detailData?.items) {
      setApprovalItemStates(detailData.items.map(it => it.estado_item === 'aprobado' ? 'aprobar' : it.estado_item === 'rechazado' ? 'rechazar' : null));
    } else {
      setApprovalItemStates([]);
    }
    setApprovalOpen(true);
  };

  const handleApproval = async () => {
    if (!approvalTarget) return;
    setApproving(true);
    try {
      const body = { accion: approvalTarget.accion, observaciones: approvalObs };
      if (approvalTarget.accion === 'parcial') {
        body.items_aprobados = approvalItemStates.map((s, i) => s === 'aprobar' ? i : -1).filter(i => i >= 0);
        body.items_rechazados = approvalItemStates.map((s, i) => s === 'rechazar' ? i : -1).filter(i => i >= 0);
      }
      await api.put(
        `/compras/pedidos-requisiciones/${approvalTarget.origen}/${approvalTarget.numero}/aprobar`,
        body
      );
      const msgs = { aprobar: 'Aprobado correctamente', rechazar: 'Rechazado', parcial: 'Aprobacion parcial aplicada' };
      toast.success(msgs[approvalTarget.accion] || 'Actualizado');
      setApprovalOpen(false);
      loadData(page);
      if (detailOpen && selectedRow) openDetail(selectedRow);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setApproving(false);
    }
  };

  const toggleItemApproval = (idx, accion) => {
    setApprovalItemStates(prev => {
      const next = [...prev];
      next[idx] = next[idx] === accion ? null : accion;
      return next;
    });
  };

  // ---- Cart helpers ----
  const itemKey = (origen, pedidoNum, item) => `${origen}-${pedidoNum}-${item.item}-${item.descripcion}`;

  const isInCart = (origen, pedidoNum, item) => {
    return cartItems.some(c => c._key === itemKey(origen, pedidoNum, item));
  };

  const toggleCartItem = (origen, pedidoNum, item, solicitanteVal) => {
    const key = itemKey(origen, pedidoNum, item);
    if (cartItems.some(c => c._key === key)) {
      setCartItems(prev => prev.filter(c => c._key !== key));
    } else {
      setCartItems(prev => [...prev, {
        _key: key,
        origen,
        pedido_numero: pedidoNum,
        item_num: item.item,
        codigo: item.codigo || '',
        descripcion: item.descripcion,
        unidad: item.unidad || 'UND',
        cantidad: item.cantidad,
        cantidad_oc: item.cantidad_oc || 0,
        ot_numero: item.ot_numero || 0,
        cuenta_gasto: item.cuenta_gasto || '',
        observaciones: item.observaciones || '',
        solicitante: solicitanteVal || '',
      }]);
    }
  };

  // Iter 433 (Renzo): un ítem está disponible para OC solo si no fue comprado
  // ni rechazado y aún le queda cantidad pendiente. En Access (sin estado_item)
  // se conserva la lógica previa por cantidad.
  const itemDisponibleParaOC = (it) => {
    if (!it) return false;
    const est = it.estado_item || '';
    if (est === 'comprado' || est === 'rechazado') return false;
    return (it.cantidad - (it.cantidad_oc || 0)) > 0;
  };

  const addAllDetailToCart = () => {
    if (!detailData || !selectedRow) return;
    const newItems = [];
    let skippedComplete = 0;
    for (const it of detailData.items || []) {
      if (!itemDisponibleParaOC(it)) { skippedComplete++; continue; }
      const key = itemKey(selectedRow.origen, selectedRow.numero, it);
      if (!cartItems.some(c => c._key === key)) {
        newItems.push({
          _key: key,
          origen: selectedRow.origen,
          pedido_numero: selectedRow.numero,
          item_num: it.item,
          codigo: it.codigo || '',
          descripcion: it.descripcion,
          unidad: it.unidad || 'UND',
          cantidad: it.cantidad,
          cantidad_oc: it.cantidad_oc || 0,
          ot_numero: it.ot_numero || 0,
          cuenta_gasto: it.cuenta_gasto || '',
          observaciones: it.observaciones || '',
          solicitante: selectedRow.solicitante || '',
        });
      }
    }
    if (newItems.length > 0) {
      setCartItems(prev => [...prev, ...newItems]);
      const msg = `${newItems.length} items agregados`;
      toast.success(skippedComplete > 0 ? `${msg} (${skippedComplete} ya completados)` : msg);
    } else if (skippedComplete > 0) {
      toast.info(`Todos los items ya tienen OC completa (${skippedComplete} completados)`);
    } else {
      toast.info('Todos los items ya estaban seleccionados');
    }
  };

  const removeCartItem = (key) => setCartItems(prev => prev.filter(c => c._key !== key));
  const clearCart = () => setCartItems([]);

  const cotizarDetailItems = () => {
    if (!detailData || !selectedRow) return;
    const items = (detailData.items || [])
      .filter(itemDisponibleParaOC)
      .map(it => ({
        descripcion: it.descripcion,
        cantidad_pendiente: Math.max(0, it.cantidad - (it.cantidad_oc || 0)),
        unidad: it.unidad || 'UND',
        codigo: it.codigo || '',
        ot_numero: it.ot_numero || '',
        requisicion: String(selectedRow.numero),
        familia: it.familia || it.familia_nombre || '',
        observacion: it.observaciones || '',
      }));
    if (items.length === 0) { toast.info('No hay items pendientes para cotizar'); return; }
    navigate(`/compras/solicitar-cotizacion?items=${encodeURIComponent(JSON.stringify(items))}`);
  };

  const goToGenerarOC = () => {
    if (cartItems.length === 0) { toast.error('Seleccione items primero'); return; }
    // Filter out items with no remaining quantity and store cart in sessionStorage
    const itemsForOC = cartItems
      .map(c => ({
        descripcion: c.descripcion,
        cantidad: Math.max(0, c.cantidad - (c.cantidad_oc || 0)),
        unidad: c.unidad,
        codigo_material: c.codigo,
        ot_numero: c.ot_numero,
        cuenta_gasto: c.cuenta_gasto,
        observaciones: `Pedido #${c.pedido_numero} (${c.origen})${c.observaciones ? ' - ' + c.observaciones : ''}`,
        requisicion_numero: c.origen === 'web' ? parseInt(c.pedido_numero) || null : null,
        pedido_origen: c.origen,
        pedido_numero_origen: c.pedido_numero,
        pedido_item_origen: c.item_num,
      }))
      .filter(i => i.cantidad > 0);
    if (itemsForOC.length === 0) {
      toast.error('Todos los items seleccionados ya tienen OC completa');
      return;
    }
    sessionStorage.setItem('oc_items_from_pedidos', JSON.stringify(itemsForOC));
    // Store solicitante metadata from the first cart item
    const solicitantes = [...new Set(cartItems.map(c => c.solicitante).filter(Boolean))];
    sessionStorage.setItem('oc_meta_from_pedidos', JSON.stringify({
      solicitante: solicitantes.join(', '),
    }));
    navigate('/compras/generar-oc');
  };

  // ---- New requisition helpers ----
  const buscarMaterial = async () => {
    if (!matSearch.trim()) return;
    setMatLoading(true);
    try {
      const res = await api.get(`/compras/materiales?q=${encodeURIComponent(matSearch)}&limit=750`);
      setMatResults(res.data.materiales || []);
    } catch { toast.error('Error buscando'); }
    finally { setMatLoading(false); }
  };

  const agregarItem = (mat = null) => {
    setItems(prev => [...prev, {
      key: Date.now() + Math.random(),
      descripcion: mat?.descripcion2 || mat?.descripcion || '',
      codigo_material: mat?.codigo || '',
      unidad: mat?.unidad || 'UND',
      cantidad: 1,
      observaciones: '',
      ot_numero: otNumero || '',  // hereda la OT global por defecto; editable por item
    }]);
    if (mat) setMatModal(false);
  };

  const updateItem = (key, field, value) => {
    setItems(prev => prev.map(i => i.key === key ? { ...i, [field]: value } : i));
  };
  const removeItem = (key) => setItems(prev => prev.filter(i => i.key !== key));

  const handleGuardar = async () => {
    if (!solicitante.trim()) { toast.error('Ingrese solicitante'); return; }
    if (items.length === 0) { toast.error('Agregue al menos un item'); return; }
    setSaving(true);
    try {
      const payload = {
        ot_numero: otNumero ? parseInt(otNumero) : null,
        solicitante,
        prioridad,
        items: items.map(i => ({
          descripcion: i.descripcion,
          cantidad: parseFloat(i.cantidad) || 1,
          unidad: i.unidad,
          codigo_material: i.codigo_material,
          observaciones: i.observaciones,
          ot_numero: i.ot_numero ? parseInt(i.ot_numero) : (otNumero ? parseInt(otNumero) : null),
        })),
      };
      let res;
      if (editingNumero) {
        res = await api.put(`/compras/requisiciones/${editingNumero}/editar`, payload);
        toast.success(res.data.message || `Requisición #${editingNumero} editada`);
      } else {
        res = await api.post('/compras/requisiciones', payload);
        toast.success(res.data.message);
      }
      setEditingNumero(null);
      setItems([]); setSolicitante(user?.nombre || ''); setOtNumero(''); setPrioridad('normal');
      setActiveTab('lista');
      loadData(0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="space-y-4" data-testid="pedidos-requisiciones-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ClipboardList className="w-6 h-6 text-purple-600" />
          Pedidos y Requisiciones
        </h1>
        <p className="text-sm text-muted-foreground">Vista unificada de pedidos (Access) y requisiciones (Web)</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        <button onClick={() => setActiveTab('lista')} data-testid="tab-lista"
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'lista' ? 'bg-white shadow-sm text-purple-700' : 'text-gray-600 hover:text-gray-800'}`}>
          <FileText className="w-4 h-4" /> Listado
        </button>
        <button onClick={() => setActiveTab('pendientes')} data-testid="tab-pendientes"
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'pendientes' ? 'bg-white shadow-sm text-orange-700' : 'text-gray-600 hover:text-gray-800'}`}>
          <Package className="w-4 h-4" /> Pendientes Compra
        </button>
        <button onClick={() => setActiveTab('recepcion')} data-testid="tab-pendientes-recepcion"
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'recepcion' ? 'bg-white shadow-sm text-teal-700' : 'text-gray-600 hover:text-gray-800'}`}>
          <Truck className="w-4 h-4" /> Pendientes Recepción
        </button>
        <button onClick={() => { if (editingNumero) cancelarEdicion(); setActiveTab('nueva'); }} data-testid="tab-nueva"
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'nueva' ? 'bg-white shadow-sm text-purple-700' : 'text-gray-600 hover:text-gray-800'}`}>
          <Plus className="w-4 h-4" /> {editingNumero ? `Editar #${editingNumero}` : 'Nueva Requisicion'}
        </button>
      </div>

      {/* ==================== PENDIENTES COMPRA TAB ==================== */}
      {activeTab === 'pendientes' && <PendientesCompra />}

      {/* ==================== PENDIENTES RECEPCIÓN TAB (Iter 359) ==================== */}
      {activeTab === 'recepcion' && <PendientesRecepcion />}

      {/* ==================== LISTA TAB ==================== */}
      {activeTab === 'lista' && (
        <>
          {/* Iter 433 (Renzo): vista Activas / Completadas */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="inline-flex items-center gap-1 bg-gray-100 p-1 rounded-lg" data-testid="vista-toggle">
              <button
                onClick={() => { setVista('activas'); setPage(0); }}
                data-testid="vista-activas"
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all flex items-center gap-1.5 ${vista === 'activas' ? 'bg-white shadow-sm text-purple-700' : 'text-gray-600 hover:text-gray-800'}`}>
                <ClipboardList className="w-4 h-4" /> Activas
              </button>
              <button
                onClick={() => { setVista('completadas'); setPage(0); }}
                data-testid="vista-completadas"
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all flex items-center gap-1.5 ${vista === 'completadas' ? 'bg-white shadow-sm text-teal-700' : 'text-gray-600 hover:text-gray-800'}`}>
                <CheckCircle className="w-4 h-4" /> Completadas
                {(stats.historicas || 0) > 0 && (
                  <span className="text-[10px] bg-teal-100 text-teal-700 rounded-full px-1.5 py-0.5 font-semibold" data-testid="vista-completadas-count">{stats.historicas}</span>
                )}
              </button>
            </div>
            {vista === 'completadas' && (
              <p className="text-xs text-gray-500 flex items-center gap-1" data-testid="vista-completadas-hint">
                <CheckCircle className="w-3.5 h-3.5 text-teal-600" />
                Requisiciones ya atendidas (todas sus líneas con OC). Consulta detalle, ítems, OC asociada, impresión y trazabilidad.
              </p>
            )}
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="kpi-cards">
            <KpiCard title="Total" value={total} icon={ClipboardList} color="bg-gray-100 text-gray-700" testId="kpi-total" />
            <KpiCard title="Pendientes" value={stats.pendientes || 0} icon={Clock} color="bg-amber-50 text-amber-600" testId="kpi-pendientes" />
            <KpiCard title="Aprobadas" value={stats.aprobadas || 0} icon={CheckCircle} color="bg-green-50 text-green-600" testId="kpi-aprobadas" />
            <KpiCard title="En Proceso" value={stats.en_proceso || 0} icon={ArrowRight} color="bg-blue-50 text-blue-600" testId="kpi-en-proceso" />
            <KpiCard title="Desde Access" value={stats.total_access || 0} icon={Database} color="bg-sky-50 text-sky-600" testId="kpi-access" />
            <KpiCard title="Desde Web" value={stats.total_web || 0} icon={Globe} color="bg-emerald-50 text-emerald-600" testId="kpi-web" />
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2" data-testid="filters-bar">
            <div className="flex items-center gap-1 text-xs font-medium text-gray-500">
              <Filter className="w-3.5 h-3.5" /> Filtros:
            </div>
            <select value={filterOrigen} onChange={(e) => setFilterOrigen(e.target.value)}
              className="h-8 px-2 rounded-md border border-input text-xs" data-testid="filter-origen">
              {ORIGEN_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {vista === 'activas' && (
              <select value={filterEstado} onChange={(e) => setFilterEstado(e.target.value)}
                className="h-8 px-2 rounded-md border border-input text-xs" data-testid="filter-estado">
                {ESTADO_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            )}
            <Input placeholder="OT..." value={filterOt} onChange={(e) => setFilterOt(e.target.value)}
              className="h-8 w-20 text-xs" data-testid="filter-ot" />
            <div className="relative flex-1 min-w-[160px] max-w-xs">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <Input placeholder="Buscar N, solicitante..." value={search}
                onChange={(e) => handleSearch(e.target.value)}
                className="h-8 pl-7 text-xs" data-testid="search-input" />
            </div>
            <Button variant="outline" size="sm" onClick={() => loadData(page)} disabled={loading} data-testid="btn-refresh">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Actualizar
            </Button>
            <Button variant="outline" size="sm" onClick={imprimirRequisiciones} title="Imprimir listado" data-testid="btn-print-requis">
              <Printer className="w-3.5 h-3.5 mr-1" /> Imprimir
            </Button>
            <Button variant="outline" size="sm" onClick={exportarRequisicionesExcel} className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" title="Exportar a Excel/CSV" data-testid="btn-excel-requis">
              <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> Excel
            </Button>
          </div>

          {/* Table */}
          {loading && data.length === 0 ? (
            <TableSkeleton rows={6} cols={6} />
          ) : data.length === 0 ? (
            <div className="text-center py-16 bg-gray-50 rounded-lg">
              <ClipboardList className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-600 text-sm">No se encontraron pedidos ni requisiciones</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-x-auto" data-testid="table-container">
              <table className="w-full text-xs">
                <thead className="sticky top-14 lg:top-0 z-20">
                  <tr className="bg-gray-800 text-white">
                    <th className="px-3 py-2.5 text-left">Origen</th>
                    <th className="px-3 py-2.5 text-left">N</th>
                    <th className="px-3 py-2.5 text-left">Fecha</th>
                    <th className="px-3 py-2.5 text-left">Solicitante</th>
                    <th className="px-3 py-2.5 text-center">OT</th>
                    <th className="px-3 py-2.5 text-center">Prioridad</th>
                    <th className="px-3 py-2.5 text-center">Items</th>
                    <th className="px-3 py-2.5 text-center">Sin OC</th>
                    <th className="px-3 py-2.5 text-center" title="Situación de cotización de la requisición">Situación Cotización</th>
                    <th className="px-3 py-2.5 text-center" title="Trazabilidad completa: SC → OC → Recepción → Factura">Trazabilidad</th>
                    <th className="px-3 py-2.5 text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, i) => (
                    <tr key={`${row.origen}-${row.numero}-${i}`}
                      className={`border-t hover:bg-gray-50 transition-colors ${row.estado === 'rechazada' ? 'bg-red-50/40' : ''}`}
                      data-testid={`row-${row.origen}-${row.numero}`}>
                      <td className="px-3 py-2"><OrigenBadge origen={row.origen} /></td>
                      <td className="px-3 py-2 font-mono font-bold text-gray-800">{row.numero}</td>
                      <td className="px-3 py-2 text-gray-600">{fmtDate(row.fecha)}</td>
                      <td className="px-3 py-2">{row.solicitante || '-'}</td>
                      <td className="px-3 py-2 text-center font-mono">{row.ot_numero || '-'}</td>
                      <td className="px-3 py-2 text-center">
                        <div className="inline-flex flex-col items-center gap-0.5">
                          <PrioridadBadge prioridad={row.prioridad} />
                          <AlertadoGerenciaBadge
                            ts={row.alerta_pendiente_enviada_at}
                            modo={row.alerta_pendiente_modo}
                            testId={`badge-alertado-gerencia-${row.numero}`}
                          />
                        </div>
                      </td>
                      <td className="px-3 py-2 text-center">{row.total_items}</td>
                      <td className="px-3 py-2 text-center">
                        {row.items_sin_oc > 0 ? (
                          <span className="text-orange-600 font-semibold">{row.items_sin_oc}</span>
                        ) : (
                          <span className="text-green-600">0</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center"><EstadoBadge estado={row.estado} /></td>
                      <td className="px-3 py-2 text-center">
                        {/* Iter 292.j — Trazabilidad completa: SC/OC/Recepción/Factura */}
                        <button
                          type="button"
                          onClick={() => openTrazabilidad(row)}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200"
                          title="Ver trazabilidad completa"
                          data-testid={`btn-trazabilidad-${row.numero}`}
                        >
                          <Eye className="w-3 h-3" /> Ver
                        </button>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => openDetail(row)} className="p-1 hover:bg-gray-100 rounded"
                            title="Ver detalle" data-testid={`btn-detail-${row.numero}`}>
                            <Eye className="w-4 h-4 text-gray-500" />
                          </button>
                          {canApprove && (row.estado === 'pendiente' || row.estado === 'parcial') && (
                            <>
                              <button onClick={() => openApproval(row, 'aprobar')}
                                className="p-1 hover:bg-green-50 rounded" title="Aprobar"
                                data-testid={`btn-aprobar-${row.numero}`}>
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              </button>
                              <button onClick={() => openApproval(row, 'rechazar')}
                                className="p-1 hover:bg-red-50 rounded" title="Rechazar"
                                data-testid={`btn-rechazar-${row.numero}`}>
                                <XCircle className="w-4 h-4 text-red-500" />
                              </button>
                            </>
                          )}
                          {/* Editar requisición (web, pendiente o desbloqueada) */}
                          {canEdit && row.origen === 'web' && ['pendiente', 'desbloqueada'].includes((row.estado || '').toLowerCase()) && (
                            <button onClick={() => cargarParaEditar(row)}
                              className="p-1 hover:bg-amber-50 rounded" title="Editar requisición"
                              data-testid={`btn-editar-req-${row.numero}`}>
                              <Pencil className="w-4 h-4 text-amber-600" />
                            </button>
                          )}
                          {/* Desbloquear requisición aprobada */}
                          {canDesbloquear && row.origen === 'web' && ['aprobada', 'parcial', 'en_proceso'].includes((row.estado || '').toLowerCase()) && (
                            <button onClick={() => abrirDesbloqueoDialog(row)}
                              className="p-1 hover:bg-orange-50 rounded" title="Desbloquear para edición"
                              data-testid={`btn-desbloquear-req-${row.numero}`}>
                              <Unlock className="w-4 h-4 text-orange-600" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between text-xs text-gray-500" data-testid="pagination">
              <span>Mostrando {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, total)} de {total}</span>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => handlePageChange(page - 1)} data-testid="btn-prev">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="px-2">Pag {page + 1} / {totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => handlePageChange(page + 1)} data-testid="btn-next">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ==================== NUEVA REQUISICION TAB ==================== */}
      {activeTab === 'nueva' && (
        <div className="space-y-4" data-testid="nueva-requisicion-form">
          {editingNumero && (
            <div className="rounded-md border-2 border-amber-300 bg-amber-50 px-3 py-2 flex items-center gap-2" data-testid="banner-editando-req">
              <Pencil className="w-4 h-4 text-amber-700 flex-shrink-0" />
              <p className="text-xs text-amber-800 flex-1">
                Editando <strong>requisición #{editingNumero}</strong>. Los cambios reemplazan los items existentes.
              </p>
              <Button size="sm" variant="ghost" onClick={() => { cancelarEdicion(); setActiveTab('lista'); }}
                className="h-7 text-xs" data-testid="btn-cancelar-edicion">
                Cancelar edición
              </Button>
            </div>
          )}
          <div className="bg-white border rounded-lg p-4">
            <h3 className="text-sm font-semibold mb-3 text-gray-700">{editingNumero ? `Editar Requisición #${editingNumero}` : 'Datos de la Requisicion'}</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <ReqSolicitanteAutocomplete value={solicitante} onChange={setSolicitante} />
              <OtAutocomplete value={otNumero} label="OT por defecto" onChange={(v) => {
                setOtNumero(v);
                // Propagar a los items que aún no tienen OT asignada
                setItems(prev => prev.map(i => (i.ot_numero === '' || i.ot_numero === null || i.ot_numero === undefined) ? { ...i, ot_numero: v } : i));
              }} />
              <div>
                <label className="block text-xs font-medium mb-1">Prioridad</label>
                <select value={prioridad} onChange={(e) => setPrioridad(e.target.value)}
                  className="w-full h-9 px-3 rounded-md border border-input text-sm" data-testid="select-prioridad">
                  <option value="normal">Normal</option>
                  <option value="urgente">Urgente</option>
                  <option value="muy_urgente">Muy Urgente</option>
                </select>
              </div>
              <div className="flex items-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setMatModal(true)} data-testid="btn-buscar-material">
                  <Search className="w-4 h-4 mr-1" /> Buscar Material
                </Button>
                <Button size="sm" onClick={() => agregarItem()} className="bg-purple-600 hover:bg-purple-700" data-testid="btn-agregar-item">
                  <Plus className="w-4 h-4 mr-1" /> Agregar
                </Button>
              </div>
            </div>
          </div>

          {items.length > 0 && (
            <div className="border rounded-lg">
              <table className="w-full text-xs">
                <thead><tr className="bg-gray-100">
                  <th className="px-2 py-2 w-8">#</th>
                  <th className="px-2 py-2 text-left">Descripcion</th>
                  <th className="px-2 py-2 w-16">Und</th>
                  <th className="px-2 py-2 w-20 text-right">Cant.</th>
                  <th className="px-2 py-2 w-24 text-center">OT</th>
                  <th className="px-2 py-2 text-left">Obs</th>
                  <th className="px-2 py-2 w-8"></th>
                </tr></thead>
                <tbody>
                  {items.map((item, idx) => (
                    <tr key={item.key} className="border-t">
                      <td className="px-2 py-1 text-center text-gray-500">{idx + 1}</td>
                      <td className="px-2 py-1">
                        <Input value={item.descripcion} onChange={(e) => updateItem(item.key, 'descripcion', e.target.value)}
                          className="h-7 text-xs" placeholder="Descripcion del material" data-testid={`item-desc-${idx}`} />
                      </td>
                      <td className="px-2 py-1">
                        <Input value={item.unidad} onChange={(e) => updateItem(item.key, 'unidad', e.target.value)}
                          className="h-7 text-xs text-center" />
                      </td>
                      <td className="px-2 py-1">
                        <NumberInput allowNegative={false} value={item.cantidad}
                          onChange={(clean) => updateItem(item.key, 'cantidad', parseFloat(clean) || 0)}
                          className="h-7 text-xs text-right" />
                      </td>
                      <td className="px-2 py-1">
                        <Input type="number" value={item.ot_numero || ''}
                          onChange={(e) => updateItem(item.key, 'ot_numero', e.target.value)}
                          className="h-7 text-xs text-center font-mono" placeholder="OT"
                          data-testid={`item-ot-${idx}`} />
                      </td>
                      <td className="px-2 py-1">
                        <Input value={item.observaciones} onChange={(e) => updateItem(item.key, 'observaciones', e.target.value)}
                          className="h-7 text-xs" placeholder="Obs" />
                      </td>
                      <td className="px-2 py-1">
                        <button onClick={() => removeItem(item.key)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {items.length > 0 && (
            <Button onClick={handleGuardar} disabled={saving} className="bg-purple-600 hover:bg-purple-700" data-testid="btn-guardar">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar Requisicion ({items.length} items)
            </Button>
          )}
        </div>
      )}

      {/* ==================== FLOATING CART BAR ==================== */}
      {cartItems.length > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white rounded-xl shadow-2xl px-5 py-3 flex items-center gap-4 min-w-[320px]"
          data-testid="cart-bar">
          <div className="flex items-center gap-2">
            <div className="bg-purple-600 rounded-full p-1.5">
              <ShoppingCart className="w-4 h-4" />
            </div>
            <div>
              <p className="text-sm font-semibold">{cartItems.length} items seleccionados</p>
              <p className="text-[10px] text-gray-400">
                {new Set(cartItems.map(c => c.pedido_numero)).size} pedidos distintos
                {cartItems.some(c => c.ot_numero > 0) && ` | ${new Set(cartItems.filter(c => c.ot_numero > 0).map(c => c.ot_numero)).size} OTs`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <Button size="sm" variant="ghost" onClick={() => setCartOpen(true)}
              className="text-gray-300 hover:text-white hover:bg-gray-700" data-testid="btn-view-cart">
              <Eye className="w-4 h-4 mr-1" /> Ver
            </Button>
            <Button size="sm" onClick={goToGenerarOC}
              className="bg-purple-600 hover:bg-purple-500" data-testid="btn-generar-oc">
              <FileText className="w-4 h-4 mr-1" /> Generar OC
            </Button>
            <button onClick={clearCart} className="p-1 hover:bg-gray-700 rounded" title="Limpiar" data-testid="btn-clear-cart">
              <XCircle className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>
      )}

      {/* ==================== DETAIL MODAL ==================== */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedRow && <OrigenBadge origen={selectedRow.origen} />}
              Pedido/Requisicion #{selectedRow?.numero}
            </DialogTitle>
            <DialogDescription>{['historico'].includes(String(selectedRow?.estado || '').toLowerCase()) ? 'Requisición completada — consulta de ítems y OC asociada' : 'Seleccione items para agregar a una Orden de Compra'}</DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-purple-600" /></div>
          ) : detailData ? (
            <div className="space-y-3">
              {/* Iter 292.c (Carlos, feb-2026) — Sello "NO APROBADA" en la
                  vista previa cuando la requisición no está aprobada.
                  Cubre pendiente, parcial, en_proceso, rechazada, etc. */}
              {selectedRow && !['aprobada', 'historico'].includes(String(selectedRow.estado || '').toLowerCase()) && (
                <div
                  className="border-2 border-red-500 bg-red-50 text-red-700 rounded-md py-2 px-4 flex items-center justify-center gap-2 uppercase font-extrabold tracking-widest text-lg"
                  data-testid="banner-no-aprobada"
                >
                  <AlertTriangle className="w-5 h-5" />
                  <span>No Aprobada</span>
                </div>
              )}

              {/* Header info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs bg-gray-50 p-3 rounded-lg">
                <div><span className="text-gray-500">Solicitante:</span><br /><strong>{selectedRow?.solicitante || '-'}</strong></div>
                <div><span className="text-gray-500">OT:</span><br /><strong>{selectedRow?.ot_numero || '-'}</strong></div>
                <div><span className="text-gray-500">Estado:</span><br />{selectedRow && <EstadoBadge estado={selectedRow.estado} />}</div>
                <div><span className="text-gray-500">Fecha:</span><br /><strong>{fmtDate(selectedRow?.fecha)}</strong></div>
              </div>

              {/* Action buttons */}
              <div className="flex gap-2 flex-wrap">
                {canApprove && (selectedRow?.estado === 'pendiente' || selectedRow?.estado === 'parcial') && (
                  <>
                    <Button size="sm" className="bg-green-600 hover:bg-green-700"
                      onClick={() => openApproval(selectedRow, 'aprobar')} data-testid="modal-btn-aprobar">
                      <CheckCircle className="w-4 h-4 mr-1" /> Aprobar Todo
                    </Button>
                    <Button size="sm" variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-50"
                      onClick={() => openApproval(selectedRow, 'parcial')} data-testid="modal-btn-parcial">
                      <AlertTriangle className="w-4 h-4 mr-1" /> Aprobacion Parcial
                    </Button>
                    <Button size="sm" variant="destructive"
                      onClick={() => openApproval(selectedRow, 'rechazar')} data-testid="modal-btn-rechazar">
                      <XCircle className="w-4 h-4 mr-1" /> Rechazar Todo
                    </Button>
                  </>
                )}
                {(detailData.items || []).length > 0 && (
                  <>
                    {!['historico'].includes(String(selectedRow?.estado || '').toLowerCase()) && (
                      <>
                        <Button size="sm" variant="outline" onClick={addAllDetailToCart} data-testid="btn-add-all-to-cart">
                          <ShoppingCart className="w-4 h-4 mr-1" /> Agregar todos a OC
                        </Button>
                        <Button size="sm" onClick={cotizarDetailItems} className="bg-[#003366] hover:bg-[#002244] gap-1" data-testid="btn-cotizar-req">
                          <Send className="w-4 h-4" /> Cotizar
                        </Button>
                      </>
                    )}
                    {/* Iter 236.r4 (Carlos): imprimir requisición individual */}
                    <Button size="sm" variant="outline" onClick={imprimirRequisicionIndividual} data-testid="btn-print-requis-individual" title="Imprimir esta requisición">
                      <Printer className="w-4 h-4 mr-1" /> Imprimir
                    </Button>
                  </>
                )}
              </div>

              {/* Items table with checkboxes */}
              <div className="border rounded-lg">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-100 text-gray-600">
                      <th className="px-2 py-2 w-8"></th>
                      <th className="px-2 py-2 w-8">#</th>
                      <th className="px-2 py-2 text-left">Codigo</th>
                      <th className="px-2 py-2 text-left">Descripcion</th>
                      <th className="px-2 py-2">Und</th>
                      <th className="px-2 py-2 text-right">Solicit.</th>
                      <th className="px-2 py-2 text-right">Con OC</th>
                      <th className="px-2 py-2 text-center">OT</th>
                      <th className="px-2 py-2 text-center">Avance</th>
                      {selectedRow?.origen === 'web' && <th className="px-2 py-2 text-center">Aprob.</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {(detailData.items || []).map((it, idx) => {
                      const pct = it.cantidad > 0 ? Math.min(100, Math.round(((it.cantidad_oc || 0) / it.cantidad) * 100)) : 0;
                      const inCart = selectedRow && isInCart(selectedRow.origen, selectedRow.numero, it);
                      const disponible = itemDisponibleParaOC(it);
                      return (
                        <tr key={idx} className={`border-t hover:bg-gray-50 ${inCart ? 'bg-purple-50' : ''}`}>
                          <td className="px-2 py-1.5 text-center">
                            {disponible ? (
                              <button onClick={() => toggleCartItem(selectedRow.origen, selectedRow.numero, it, selectedRow?.solicitante)}
                                className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                                  inCart ? 'bg-purple-600 border-purple-600 text-white' : 'border-gray-300 hover:border-purple-400'
                                }`} data-testid={`checkbox-item-${idx}`}>
                                {inCart && <Check className="w-3 h-3" />}
                              </button>
                            ) : it.estado_item === 'rechazado' ? (
                              <span className="text-red-300" title="Rechazado"><XCircle className="w-4 h-4" /></span>
                            ) : (
                              <span className="text-green-500" title="Con OC"><CheckCircle className="w-4 h-4" /></span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-center text-gray-400">{it.item}</td>
                          <td className="px-2 py-1.5 font-mono text-gray-500 text-[10px]">{it.codigo ? it.codigo.slice(-8) : '-'}</td>
                          <td className="px-2 py-1.5">
                            <div>{it.descripcion}</div>
                            {it.observaciones && <div className="text-[10px] text-gray-400 italic">{it.observaciones}</div>}
                          </td>
                          <td className="px-2 py-1.5 text-center">{it.unidad}</td>
                          <td className="px-2 py-1.5 text-right font-mono">{it.cantidad}</td>
                          <td className="px-2 py-1.5 text-right font-mono">{it.cantidad_oc || 0}</td>
                          <td className="px-2 py-1.5 text-center font-mono text-gray-500">{it.ot_numero || '-'}</td>
                          <td className="px-2 py-1.5">
                            <div className="flex items-center gap-1">
                              <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                                <div className={`h-1.5 rounded-full ${pct >= 100 ? 'bg-green-500' : pct > 0 ? 'bg-blue-500' : 'bg-gray-300'}`}
                                  style={{ width: `${pct}%` }} />
                              </div>
                              <span className={`text-[10px] w-8 text-right ${pct >= 100 ? 'text-green-600 font-semibold' : 'text-gray-500'}`}>{pct}%</span>
                            </div>
                          </td>
                          {selectedRow?.origen === 'web' && (
                            <td className="px-2 py-1.5 text-center">
                              {it.estado_item === 'aprobado' ? (
                                it.repuesto_por_anulacion_oc ? (
                                  <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-purple-100 text-purple-700 whitespace-nowrap" data-testid={`item-repuesto-${idx}`}
                                    title={`Vuelve por anulación de la OC ${it.repuesto_por_anulacion_oc}`}>
                                    ↩ Anulación OC {it.repuesto_por_anulacion_oc}
                                  </span>
                                ) : (
                                  <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-green-100 text-green-700" data-testid={`item-aprobado-${idx}`}>OK</span>
                                )
                              ) : it.estado_item === 'rechazado' ? (
                                <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-red-100 text-red-700" data-testid={`item-rechazado-${idx}`}>NO</span>
                              ) : it.estado_item === 'comprado' ? (
                                <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-blue-100 text-blue-700" data-testid={`item-comprado-${idx}`} title={`Comprado en OC ${it.oc_numero || ''}`}>OC {it.oc_numero || '✓'}</span>
                              ) : (
                                <span className="text-gray-300 text-[10px]">-</span>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-500">{detailData.total_items} items en total</p>
            </div>
          ) : (
            <p className="text-sm text-gray-500 py-4">Sin datos</p>
          )}
        </DialogContent>
      </Dialog>

      {/* ==================== CART MODAL ==================== */}
      <Dialog open={cartOpen} onOpenChange={setCartOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-purple-600" />
              Items seleccionados para OC ({cartItems.length})
            </DialogTitle>
            <DialogDescription>
              Items de {new Set(cartItems.map(c => c.pedido_numero)).size} pedidos distintos.
              La cantidad se calcula como pendiente (solicitado - ya con OC).
            </DialogDescription>
          </DialogHeader>
          {cartItems.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">No hay items seleccionados</p>
          ) : (
            <div className="border rounded-lg">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-100 text-gray-600">
                    <th className="px-2 py-2 text-left">Pedido</th>
                    <th className="px-2 py-2 text-left">Descripcion</th>
                    <th className="px-2 py-2">Und</th>
                    <th className="px-2 py-2 text-right">Pendiente</th>
                    <th className="px-2 py-2 text-center">OT</th>
                    <th className="px-2 py-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {cartItems.map((c) => (
                    <tr key={c._key} className="border-t hover:bg-gray-50">
                      <td className="px-2 py-1.5">
                        <span className="font-mono font-semibold">#{c.pedido_numero}</span>
                        <span className="text-[10px] text-gray-400 ml-1">({c.origen})</span>
                      </td>
                      <td className="px-2 py-1.5">{c.descripcion}</td>
                      <td className="px-2 py-1.5 text-center">{c.unidad}</td>
                      <td className="px-2 py-1.5 text-right font-mono font-semibold">{c.cantidad - (c.cantidad_oc || 0)}</td>
                      <td className="px-2 py-1.5 text-center font-mono text-gray-500">{c.ot_numero || '-'}</td>
                      <td className="px-2 py-1.5">
                        <button onClick={() => removeCartItem(c._key)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={clearCart}>Limpiar todo</Button>
            <Button onClick={() => { setCartOpen(false); goToGenerarOC(); }}
              className="bg-purple-600 hover:bg-purple-700" disabled={cartItems.length === 0} data-testid="btn-cart-generar-oc">
              <FileText className="w-4 h-4 mr-1" /> Ir a Generar OC
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== APPROVAL MODAL ==================== */}
      <Dialog open={approvalOpen} onOpenChange={setApprovalOpen}>
        <DialogContent className={`${approvalTarget?.accion === 'parcial' ? 'max-w-2xl' : 'max-w-md'} max-h-[80vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {approvalTarget?.accion === 'aprobar' ? (
                <><CheckCircle className="w-5 h-5 text-green-600" /> Aprobar Pedido</>
              ) : approvalTarget?.accion === 'parcial' ? (
                <><AlertTriangle className="w-5 h-5 text-amber-500" /> Aprobacion Parcial</>
              ) : (
                <><AlertTriangle className="w-5 h-5 text-red-500" /> Rechazar Pedido</>
              )}
            </DialogTitle>
            <DialogDescription>
              {approvalTarget?.accion === 'parcial'
                ? `Seleccione items a aprobar o rechazar del pedido #${approvalTarget?.numero}`
                : approvalTarget?.accion === 'aprobar'
                  ? `Confirmar aprobacion del pedido/requisicion #${approvalTarget?.numero}`
                  : `Confirmar rechazo del pedido/requisicion #${approvalTarget?.numero}`}
            </DialogDescription>
          </DialogHeader>

          {/* Partial approval: item-level selection */}
          {approvalTarget?.accion === 'parcial' && detailData?.items && (
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-100 text-gray-600">
                    <th className="px-2 py-2 text-left">#</th>
                    <th className="px-2 py-2 text-left">Descripcion</th>
                    <th className="px-2 py-2 text-right">Cant.</th>
                    <th className="px-2 py-2 text-center w-20">OT</th>
                    <th className="px-2 py-2 text-center w-24">Accion</th>
                  </tr>
                </thead>
                <tbody>
                  {detailData.items.map((it, idx) => (
                    <tr key={idx} className={`border-t ${approvalItemStates[idx] === 'aprobar' ? 'bg-green-50' : approvalItemStates[idx] === 'rechazar' ? 'bg-red-50' : ''}`}>
                      <td className="px-2 py-1.5 text-gray-400">{it.item}</td>
                      <td className="px-2 py-1.5">{it.descripcion}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{it.cantidad}</td>
                      <td className="px-2 py-1.5 text-center font-mono text-gray-500">{it.ot_numero || '-'}</td>
                      <td className="px-2 py-1.5">
                        <div className="flex gap-1 justify-center">
                          <button onClick={() => toggleItemApproval(idx, 'aprobar')}
                            className={`w-6 h-6 rounded flex items-center justify-center transition-colors ${
                              approvalItemStates[idx] === 'aprobar' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-400 hover:bg-green-100'
                            }`} data-testid={`partial-approve-${idx}`}>
                            <Check className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => toggleItemApproval(idx, 'rechazar')}
                            className={`w-6 h-6 rounded flex items-center justify-center transition-colors ${
                              approvalItemStates[idx] === 'rechazar' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-400 hover:bg-red-100'
                            }`} data-testid={`partial-reject-${idx}`}>
                            <XCircle className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-3 py-2 bg-gray-50 border-t text-[10px] text-gray-500 flex gap-3">
                <span className="text-green-600 font-semibold">{approvalItemStates.filter(s => s === 'aprobar').length} aprobados</span>
                <span className="text-red-600 font-semibold">{approvalItemStates.filter(s => s === 'rechazar').length} rechazados</span>
                <span>{approvalItemStates.filter(s => !s).length} sin decision</span>
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium mb-1">Observaciones (opcional)</label>
            <textarea value={approvalObs} onChange={(e) => setApprovalObs(e.target.value)}
              className="w-full rounded-md border border-input px-3 py-2 text-sm min-h-[60px]"
              placeholder="Agregar comentario..." data-testid="approval-obs" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApprovalOpen(false)}>Cancelar</Button>
            <Button disabled={approving} onClick={handleApproval}
              className={approvalTarget?.accion === 'aprobar' ? 'bg-green-600 hover:bg-green-700' : approvalTarget?.accion === 'parcial' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-red-600 hover:bg-red-700'}
              data-testid="btn-confirm-approval">
              {approving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {approvalTarget?.accion === 'aprobar' ? 'Confirmar Aprobacion' : approvalTarget?.accion === 'parcial' ? 'Aplicar Aprobacion Parcial' : 'Confirmar Rechazo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== MATERIAL SEARCH MODAL ==================== */}
      <Dialog open={matModal} onOpenChange={setMatModal}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Buscar Material</DialogTitle></DialogHeader>
          <div className="flex gap-2 mb-3">
            <Input placeholder="Buscar por descripcion o codigo..." value={matSearch}
              onChange={(e) => setMatSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && buscarMaterial()} data-testid="material-search-input" />
            <Button onClick={buscarMaterial} disabled={matLoading} data-testid="btn-material-search">
              {matLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>
          {matResults.length > 0 && (
            <div className="px-2 py-1 bg-gray-50 border-b text-[11px] text-gray-500 sticky top-0 z-10">
              {matResults.length} materiales encontrados
            </div>
          )}
          <div className="space-y-1 max-h-[55vh] overflow-y-auto">
            {matResults.map((m, i) => {
              const fullDesc = m.descripcion2 || m.descripcion || '';
              return (
              <div key={i} onClick={() => agregarItem(m)}
                className="p-2 bg-gray-50 rounded hover:bg-purple-50 cursor-pointer flex justify-between items-center"
                data-testid={`material-result-${i}`}>
                <div>
                  <p className="text-sm font-medium">{fullDesc}</p>
                  <p className="text-xs text-gray-500">{m.unidad}{m.medida ? ` | Medida: ${m.medida}` : ''}{m.familia_nombre ? ` | ${m.familia_nombre}` : ''}</p>
                </div>
                <Plus className="w-5 h-5 text-green-600 shrink-0" />
              </div>
              );
            })}
            {matResults.length === 0 && matSearch && !matLoading && (
              <p className="text-sm text-gray-500 text-center py-4">Sin resultados</p>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setMatModal(false)}>Cerrar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Desbloquear Requisición */}
      <Dialog open={desbloquearOpen} onOpenChange={setDesbloquearOpen}>
        <DialogContent className="max-w-md" data-testid="desbloquear-req-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-700">
              <Unlock className="w-5 h-5" /> Desbloquear Requisición #{desbloquearTarget?.numero}
            </DialogTitle>
            <DialogDescription>
              La requisición vuelve a estado editable y podrá ser modificada por su creador o por compras.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md bg-orange-50 border border-orange-200 px-3 py-2 text-xs flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
              <p className="text-orange-800">
                Estado actual: <strong>{desbloquearTarget?.estado || '—'}</strong>. Al desbloquear, pasará a <strong>desbloqueada</strong> (editable).
              </p>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block" htmlFor="desbloquear-req-motivo">
                Motivo del desbloqueo <span className="text-red-500">*</span>
              </label>
              <textarea id="desbloquear-req-motivo" value={desbloquearMotivo}
                onChange={(e) => setDesbloquearMotivo(e.target.value)}
                placeholder="Ej: solicitante necesita ajustar cantidad de material X."
                rows={3}
                className="w-full px-3 py-2 rounded-md border border-input text-sm resize-none"
                data-testid="desbloquear-req-motivo" />
              <p className="text-[11px] text-gray-500 mt-1">Mínimo 5 caracteres. Queda en historial.</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDesbloquearOpen(false)} disabled={desbloquearLoading}>Cancelar</Button>
            <Button onClick={handleDesbloquear} disabled={desbloquearLoading || desbloquearMotivo.trim().length < 5}
              className="bg-orange-600 hover:bg-orange-700 gap-1.5"
              data-testid="desbloquear-req-confirmar">
              {desbloquearLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlock className="w-4 h-4" />}
              Desbloquear
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 292.j (Carlos, feb-2026) — Modal Trazabilidad completa */}
      <Dialog open={trazaOpen} onOpenChange={(v) => !v && closeTrazabilidad()}>
        <DialogContent
          className="max-w-3xl max-h-[85vh] overflow-y-auto"
          data-testid="trazabilidad-modal"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-indigo-600" />
              Trazabilidad · Requisición #{trazaRow?.numero}
            </DialogTitle>
          </DialogHeader>
          {trazaLoading ? (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" />
              <p className="text-sm text-gray-500 mt-2">Cargando trazabilidad...</p>
            </div>
          ) : trazaData ? (
            <TrazabilidadContent data={trazaData} />
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={closeTrazabilidad} data-testid="close-trazabilidad">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Iter 292.j — Contenido del modal de trazabilidad.
 * Estructura: Situación → Cotización → OC → Recepción → Factura.
 */
function TrazabilidadContent({ data }) {
  const req = data.requisicion || {};
  const cots = data.cotizaciones || [];
  const ocs = data.ordenes_compra || [];
  const recs = data.recepciones || [];
  const facs = data.facturas || [];

  // Situación de la requisición
  const estadoReq = String(req.estado || '').toLowerCase();
  const situacionLabel = {
    aprobada: 'Autorizada',
    en_proceso: 'En Proceso',
    pendiente: 'Pendiente',
    rechazada: 'Rechazada',
    desbloqueada: 'Desbloqueada',
  }[estadoReq] || (req.estado || '—').toUpperCase();
  const situacionColor = {
    aprobada: 'bg-green-100 text-green-800 border-green-300',
    en_proceso: 'bg-blue-100 text-blue-800 border-blue-300',
    pendiente: 'bg-amber-100 text-amber-800 border-amber-300',
    rechazada: 'bg-red-100 text-red-800 border-red-300',
    desbloqueada: 'bg-orange-100 text-orange-800 border-orange-300',
  }[estadoReq] || 'bg-gray-100 text-gray-800 border-gray-300';

  const fmt = (v) => v ? fmtDate(v) : '—';
  const fmtDateTime = (v) => {
    if (!v) return '—';
    try {
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return String(v);
      return d.toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return String(v); }
  };
  const fmtMoney = (n) => typeof n === 'number' ? `$${n.toLocaleString('es-CL', { maximumFractionDigits: 0 })}` : '—';

  const estadoOcLabel = {
    autorizada: 'Autorizada',
    pendiente_autorizacion: 'Pendiente de Autorización',
    anulada: 'Anulada',
  };
  const estadoOcColor = {
    autorizada: 'bg-green-100 text-green-800 border-green-300',
    pendiente_autorizacion: 'bg-amber-100 text-amber-800 border-amber-300',
    anulada: 'bg-red-100 text-red-800 border-red-300',
  };

  const estadoScLabel = {
    borrador: 'Borrador',
    enviada: 'Enviada (en proceso)',
    parcial: 'Cotización Parcial',
    completa: 'Cotizada',
    cerrada: 'Cerrada',
  };
  const estadoScColor = {
    borrador: 'bg-gray-100 text-gray-700',
    enviada: 'bg-blue-100 text-blue-800',
    parcial: 'bg-yellow-100 text-yellow-800',
    completa: 'bg-green-100 text-green-800',
    cerrada: 'bg-slate-200 text-slate-800',
  };

  const estadoFacLabel = {
    pagada: 'Pagada',
    pendiente: 'Pendiente de Pago',
    parcial: 'Pago Parcial',
    anulada_nc: 'Anulada por NC',
    sin_datos: 'Sin datos de pago',
  };
  const estadoFacColor = {
    pagada: 'bg-green-100 text-green-800 border-green-300',
    pendiente: 'bg-orange-100 text-orange-800 border-orange-300',
    parcial: 'bg-yellow-100 text-yellow-900 border-yellow-300',
    anulada_nc: 'bg-red-100 text-red-800 border-red-300',
    sin_datos: 'bg-gray-100 text-gray-600 border-gray-300',
  };

  return (
    <div className="space-y-4 text-sm">
      {/* 1) Situación de la Requisición */}
      <section data-testid="traza-situacion">
        <h3 className="font-semibold text-gray-800 mb-1.5">Situación</h3>
        <div className={`inline-flex items-center gap-2 px-2.5 py-1 rounded border text-sm font-semibold ${situacionColor}`}>
          {situacionLabel}
        </div>
        {estadoReq === 'aprobada' && (
          <p className="text-xs text-gray-600 mt-1.5">
            Autorizada por <strong>{req.aprobado_por || '—'}</strong> el <strong>{fmtDateTime(req.fecha_aprobacion)}</strong>
          </p>
        )}
        {estadoReq === 'rechazada' && (
          <p className="text-xs text-gray-600 mt-1.5">
            Rechazada por <strong>{req.rechazado_por || '—'}</strong> el <strong>{fmtDateTime(req.fecha_rechazo)}</strong>
            {req.motivo_rechazo && <span> · Motivo: <em>{req.motivo_rechazo}</em></span>}
          </p>
        )}
        <p className="text-xs text-gray-500 mt-1">
          Creada por {req.solicitante || req.creado_por || '—'} el {fmtDateTime(req.fecha_creacion)}
        </p>
      </section>

      {/* 2) Cotización(es) */}
      <section data-testid="traza-cotizacion">
        <h3 className="font-semibold text-gray-800 mb-1.5">Cotización</h3>
        {cots.length === 0 ? (
          <p className="text-xs text-gray-500 italic">Sin solicitud de cotización asociada</p>
        ) : (
          <div className="space-y-1.5">
            {cots.map((sc) => (
              <div key={sc.id} className="border rounded px-2.5 py-1.5 bg-slate-50" data-testid={`traza-sc-${sc.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono font-semibold text-slate-800">SC-{sc.numero || sc.id?.slice(0,8)}</span>
                  <span className={`px-1.5 rounded text-[10px] font-medium ${estadoScColor[sc.estado] || 'bg-gray-100 text-gray-700'}`}>
                    {estadoScLabel[sc.estado] || (sc.estado || '').toUpperCase()}
                  </span>
                </div>
                <p className="text-[11px] text-gray-600 mt-0.5">
                  Creada por {sc.creado_por || '—'} el {fmtDateTime(sc.fecha_creacion)}
                  {sc.fecha_envio && <> · Enviada el {fmtDateTime(sc.fecha_envio)}</>}
                </p>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* 3) Órdenes de Compra */}
      <section data-testid="traza-oc">
        <h3 className="font-semibold text-gray-800 mb-1.5">Orden de Compra</h3>
        {ocs.length === 0 ? (
          <p className="text-xs text-gray-500 italic">Sin OC asociada</p>
        ) : (
          <div className="space-y-1.5">
            {ocs.map((oc) => (
              <div key={oc.numero_oc} className="border rounded px-2.5 py-1.5 bg-blue-50/40" data-testid={`traza-oc-${oc.numero_oc}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono font-semibold text-blue-800">OC N° {oc.numero_oc}</span>
                  <span className={`px-1.5 rounded text-[10px] font-medium border ${estadoOcColor[oc.estado] || 'bg-gray-100 text-gray-700'}`}>
                    {estadoOcLabel[oc.estado] || (oc.estado || '').toUpperCase()}
                  </span>
                </div>
                <p className="text-[11px] text-gray-700 mt-0.5">
                  {oc.nombre_proveedor && <><strong>{oc.nombre_proveedor}</strong> · </>}
                  {fmt(oc.fecha)} · Total {fmtMoney(oc.total)}
                </p>
                {oc.estado === 'autorizada' && (
                  <p className="text-[11px] text-green-700 mt-0.5">
                    Autorizada por <strong>{oc.aprobada_por || '—'}</strong> el <strong>{fmtDateTime(oc.fecha_aprobacion)}</strong>
                  </p>
                )}
                {oc.estado === 'anulada' && (
                  <p className="text-[11px] text-red-700 mt-0.5">
                    Anulada por <strong>{oc.anulada_por || '—'}</strong> el <strong>{fmtDateTime(oc.fecha_anulacion)}</strong>
                  </p>
                )}
                {oc.estado === 'pendiente_autorizacion' && (
                  <p className="text-[11px] text-amber-700 mt-0.5">
                    Creada por {oc.creado_por || '—'} — esperando VB de Gerencia
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* 4) Recepciones */}
      <section data-testid="traza-recepciones">
        <h3 className="font-semibold text-gray-800 mb-1.5">Recepción</h3>
        {recs.length === 0 ? (
          <p className="text-xs text-gray-500 italic">Sin recepciones registradas</p>
        ) : (
          <div className="space-y-1.5">
            {recs.map((r, i) => (
              <div key={`rec-${r.numero_recepcion}-${i}`} className="border rounded px-2.5 py-1.5 bg-emerald-50/40" data-testid={`traza-rec-${r.numero_recepcion}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono font-semibold text-emerald-800">Doc. Recepción N° {r.numero_recepcion}</span>
                  <span className="text-[11px] text-emerald-700">{fmtDateTime(r.fecha)}</span>
                </div>
                <p className="text-[11px] text-gray-700 mt-0.5">
                  OC {r.numero_oc}
                  {r.responsable && <> · Recibido por {r.responsable}</>}
                  {typeof r.monto === 'number' && <> · Monto {fmtMoney(r.monto)}</>}
                </p>
                {r.guia_despacho && (
                  <p className="text-[11px] text-emerald-800 mt-0.5">
                    Guía de despacho: <strong>{r.guia_despacho}</strong>
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* 5) Facturas */}
      <section data-testid="traza-facturas">
        <h3 className="font-semibold text-gray-800 mb-1.5">Factura</h3>
        {facs.length === 0 ? (
          <p className="text-xs text-gray-500 italic">Sin factura asociada</p>
        ) : (
          <div className="space-y-1.5">
            {facs.map((f, i) => (
              <div key={`fac-${f.folio}-${i}`} className="border rounded px-2.5 py-1.5 bg-amber-50/40" data-testid={`traza-fac-${f.folio}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono font-semibold text-amber-800">
                    {f.tipo_documento || 'Factura'} N° {f.folio}
                  </span>
                  <span className={`px-1.5 rounded text-[10px] font-medium border ${estadoFacColor[f.estado] || 'bg-gray-100 text-gray-600 border-gray-300'}`}>
                    {estadoFacLabel[f.estado] || (f.estado || '').toUpperCase()}
                  </span>
                </div>
                <p className="text-[11px] text-gray-700 mt-0.5">
                  {f.numero_oc && <>OC {f.numero_oc}</>}
                  {f.fecha_emision && <> · Emitida {fmt(f.fecha_emision)}</>}
                  {typeof f.total === 'number' && <> · Total {fmtMoney(f.total)}</>}
                </p>
                {f.estado === 'pagada' && f.fecha_pago_real && (
                  <p className="text-[11px] text-green-700 mt-0.5">Pagada el {fmt(f.fecha_pago_real)}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
